'use client';

import { Fragment } from 'react';
import { motion } from 'motion/react';
import { Check, FileSearch, Scan, ShieldCheck } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { cn } from '@/lib/utils';

import type { OcrProgressStage } from '../types';
import { OcrProgressBar } from './ocr-progress-bar';

export type StepState = 'done' | 'active' | 'waiting';

interface ExtractionTimelineProps {
  stepStates: [StepState, StepState, StepState];
  /** Live page progress (0..total). When `totalPages > 0` the live bar is shown. */
  currentPage?: number;
  totalPages?: number;
  progressStage?: OcrProgressStage | null;
}

const STEP_CONFIG = [
  { key: 'analyzing' as const, icon: Scan },
  { key: 'extractingStatus' as const, icon: FileSearch },
  { key: 'verificationStep' as const, icon: ShieldCheck }
];

export function ExtractionTimeline({
  stepStates,
  currentPage,
  totalPages,
  progressStage
}: ExtractionTimelineProps) {
  const t = useTranslations('ocr');
  const showProgress = (totalPages ?? 0) > 0;

  return (
    <div className='flex w-full flex-col gap-3'>
      <div className='flex items-start'>
        {STEP_CONFIG.map((step, i) => {
          const state = stepStates[i];
          const isDone = state === 'done';
          const isActive = state === 'active';
          const Icon = isDone ? Check : step.icon;

          return (
            <Fragment key={step.key}>
              {/* Connecting line */}
              {i > 0 && (
                <div
                  className={cn(
                    'mt-3.5 h-[1.5px] flex-1 transition-colors duration-500',
                    isDone || isActive ? 'bg-primary/40' : 'bg-border'
                  )}
                />
              )}

              {/* Step node */}
              <div className='flex flex-col items-center gap-1.5 px-1'>
                <div
                  className={cn(
                    'flex h-7 w-7 items-center justify-center rounded-full border transition-all duration-500',
                    isDone &&
                      'border-emerald-500/30 bg-emerald-500/10 text-emerald-500',
                    isActive && 'border-primary/30 bg-primary/10 text-primary',
                    !isDone &&
                      !isActive &&
                      'border-border bg-muted/50 text-muted-foreground'
                  )}
                >
                  {isActive ? (
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: 'easeInOut'
                      }}
                    >
                      <Icon className='h-3.5 w-3.5' />
                    </motion.div>
                  ) : (
                    <Icon className='h-3.5 w-3.5' />
                  )}
                </div>
                <span
                  className={cn(
                    'text-[10px] leading-none font-medium',
                    isDone && 'text-emerald-600 dark:text-emerald-400',
                    isActive && 'text-primary',
                    !isDone && !isActive && 'text-muted-foreground'
                  )}
                >
                  {t(step.key)}
                </span>
              </div>
            </Fragment>
          );
        })}
      </div>
      {showProgress && (
        <OcrProgressBar
          currentPage={currentPage ?? 0}
          totalPages={totalPages ?? 0}
          stage={progressStage ?? null}
        />
      )}
    </div>
  );
}
